import type { ComponentPropsWithoutRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/cn';

/**
 * `FE-04.10` primitive — resolved in `docs/design/stage2/B3-BRIEF.md` §4.
 *
 * Stays a standalone primitive. entry-surfaces offered to fold it into
 * `FE-04.11`'s pending boundary; the brief keeps it separate because
 * finding-things and card-back both need it where the shell never sees it —
 * inside an open `Popover`/`Combobox` panel, and inside an already-open card
 * back refreshing its own comment page. Both give a correctness reason, not
 * an aesthetic one: a spinner inside `role="listbox"` is an option with no
 * value and breaks `aria-activedescendant`; the card back opens from a cold
 * permalink with nothing in cache, so its loading state must preserve layout
 * rather than flash.
 *
 * `shape` (not `variant` — everywhere else in this set `variant` means a
 * *visual* variant of one thing, and these are different geometries) is the
 * union of all three returns' shape lists, as resolved in the brief: `line`,
 * `block`, `row`, `avatar`, `chip`.
 *
 * `count` (not `lines`) because it repeats rows as well as lines.
 *
 * `aria-hidden` always — a skeleton carries no information a screen reader
 * should read; the caller marks the *region* `aria-busy="true"` and, where
 * useful, adds one visually-hidden live-region line ("Loading…"). `Skeleton`
 * itself never announces anything.
 *
 * Respects `prefers-reduced-motion` via Tailwind's `motion-safe:` variant
 * (`@media (prefers-reduced-motion: no-preference)`), so a member who has
 * asked the OS for reduced motion gets a static placeholder instead of a
 * pulsing one. jsdom evaluates no media queries, so this cannot be asserted
 * in this file's tests — a real-browser claim, noted rather than faked with
 * a test that would pass regardless of whether the CSS is even correct.
 */
const skeletonShapeVariants = cva('motion-safe:animate-pulse bg-border', {
  variants: {
    shape: {
      line: 'h-3 w-full rounded-full',
      block: 'h-24 w-full rounded-card',
      row: 'h-12 w-full rounded-card',
      avatar: 'size-8 rounded-full',
      chip: 'h-6 w-20 rounded-full',
    },
  },
});

export interface SkeletonProps
  extends
    Omit<ComponentPropsWithoutRef<'div'>, 'aria-hidden'>,
    VariantProps<typeof skeletonShapeVariants> {
  shape: 'line' | 'block' | 'row' | 'avatar' | 'chip';
  /** Number of placeholder instances to render. Default 1. */
  count?: number;
  /** Overrides the shape's default width (e.g. a shorter final line). */
  width?: string | number;
}

export function Skeleton({ shape, count = 1, width, className, ...rest }: SkeletonProps) {
  // A caller passing a computed length (e.g. the page size it is about to
  // fetch) can legitimately pass 0 — render nothing rather than one phantom
  // row nobody asked for.
  if (count <= 0) {
    return null;
  }

  const items = Array.from({ length: count });
  // Avatars and chips read naturally as a horizontal cluster (an avatar
  // stack, a row of filter chips); every other shape stacks vertically, the
  // way the rows/lines they stand in for actually lay out.
  const layout =
    shape === 'avatar' || shape === 'chip'
      ? 'flex flex-row flex-wrap gap-2'
      : 'flex flex-col gap-2';

  return (
    <div aria-hidden="true" className={cn(layout, className)} {...rest}>
      {items.map((_, index) => (
        <span
          key={index}
          className={skeletonShapeVariants({ shape })}
          style={width !== undefined ? { width } : undefined}
        />
      ))}
    </div>
  );
}
